import { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  User,
  Shield,
  MessageCircle,
  Menu,
  X,
  LogOut,
  ChevronDown,
  Globe,
  PanelLeftClose,
  PanelLeft,
  CreditCard,
  BarChart3,
} from 'lucide-react';
import { useSession, signOut } from '@/lib/auth-client';
import { useI18n, localeNames, Locale, useLocalizedPath } from '@/lib/i18n';
import { Logo } from '@/components/ui/Logo';
import { ThemeSwitcher } from '@/components/ui/ThemeSwitcher';
import './DashboardLayout.scss';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, labelKey: 'dashboard.nav.dashboard' },
  { path: '/dashboard/profile', icon: User, labelKey: 'dashboard.nav.profile' },
  { path: '/dashboard/security', icon: Shield, labelKey: 'dashboard.nav.security' },
  { path: '/dashboard/billing', icon: CreditCard, labelKey: 'dashboard.nav.billing' },
  { path: '/dashboard/usage', icon: BarChart3, labelKey: 'dashboard.nav.usage' },
  { path: '/dashboard/feedback', icon: MessageCircle, labelKey: 'dashboard.nav.feedback' },
];

export function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const { t, locale, setLocale } = useI18n();
  const lp = useLocalizedPath();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  const toggleSidebarCollapse = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', String(newState));
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/sign-in', { replace: true });
  };

  const handleLocaleChange = (newLocale: Locale) => {
    setLocale(newLocale);
    setLangMenuOpen(false);
  };

  const user = session?.user;

  return (
    <div className="dashboard-layout">
      {/* Mobile header */}
      <header className="dashboard-layout__header">
        <button
          className="dashboard-layout__menu-btn"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label="Toggle menu"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        <Link to="/dashboard" className="dashboard-layout__logo">
          <Logo size={32} />
          <span>Sokuji</span>
        </Link>

        <div className="dashboard-layout__header-actions">
          {/* Theme Switcher */}
          <ThemeSwitcher variant="compact" />

          {/* Language Selector */}
          <div className="dashboard-layout__lang-menu">
            <button
              className="dashboard-layout__lang-btn"
              onClick={() => setLangMenuOpen(!langMenuOpen)}
            >
              <Globe size={18} />
              <ChevronDown size={14} />
            </button>

            {langMenuOpen && (
              <div className="dashboard-layout__lang-dropdown">
                {(Object.keys(localeNames) as Locale[]).map((loc) => (
                  <button
                    key={loc}
                    className={`dashboard-layout__lang-option ${loc === locale ? 'dashboard-layout__lang-option--active' : ''}`}
                    onClick={() => handleLocaleChange(loc)}
                  >
                    {localeNames[loc]}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="dashboard-layout__user-menu">
            <button
              className="dashboard-layout__user-btn"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <div className="dashboard-layout__avatar">
                {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
              </div>
              <ChevronDown size={16} />
            </button>

            {userMenuOpen && (
              <div className="dashboard-layout__dropdown">
                <div className="dashboard-layout__dropdown-header">
                  <strong>{user?.name || t('dashboard.user.fallback')}</strong>
                  <span>{user?.email}</span>
                </div>
                <div className="dashboard-layout__dropdown-divider" />
                <button onClick={handleSignOut} className="dashboard-layout__dropdown-item">
                  <LogOut size={16} />
                  {t('dashboard.nav.signOut')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`dashboard-layout__sidebar ${sidebarOpen ? 'dashboard-layout__sidebar--open' : ''} ${sidebarCollapsed ? 'dashboard-layout__sidebar--collapsed' : ''}`}>
        <div className="dashboard-layout__sidebar-header">
          <Link to="/dashboard" className="dashboard-layout__logo">
            <Logo size={32} />
            <span>Sokuji</span>
          </Link>
          {/* Collapse button in header - only when expanded */}
          {!sidebarCollapsed && (
            <button
              className="dashboard-layout__collapse-btn dashboard-layout__collapse-btn--header"
              onClick={toggleSidebarCollapse}
              aria-label="Collapse sidebar"
              title={t('dashboard.sidebar.collapse')}
            >
              <PanelLeftClose size={20} />
            </button>
          )}
        </div>

        <nav className="dashboard-layout__nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const label = t(item.labelKey);

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`dashboard-layout__nav-item ${isActive ? 'dashboard-layout__nav-item--active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                title={sidebarCollapsed ? label : undefined}
              >
                <Icon size={20} />
                <span>{label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="dashboard-layout__sidebar-footer">
          {/* Show these items only when sidebar is expanded */}
          {!sidebarCollapsed && (
            <>
              {/* Legal Links */}
              <div className="dashboard-layout__sidebar-links">
                <Link to={lp('/docs/terms')} className="dashboard-layout__sidebar-link">
                  {t('nav.terms')}
                </Link>
                <Link to={lp('/docs/privacy')} className="dashboard-layout__sidebar-link">
                  {t('nav.privacy')}
                </Link>
                <Link to={lp('/docs/commerce')} className="dashboard-layout__sidebar-link">
                  {t('nav.commerce')}
                </Link>
              </div>

              {/* Theme Switcher in Sidebar */}
              <ThemeSwitcher variant="full" className="theme-switcher--sidebar" />

              {/* Language Selector in Sidebar */}
              <div className="dashboard-layout__sidebar-lang">
                <button
                  className="dashboard-layout__sidebar-lang-btn"
                  onClick={() => setLangMenuOpen(!langMenuOpen)}
                >
                  <Globe size={18} />
                  <span>{localeNames[locale]}</span>
                  <ChevronDown size={14} />
                </button>

                {langMenuOpen && (
                  <div className="dashboard-layout__sidebar-lang-dropdown">
                    {(Object.keys(localeNames) as Locale[]).map((loc) => (
                      <button
                        key={loc}
                        className={`dashboard-layout__lang-option ${loc === locale ? 'dashboard-layout__lang-option--active' : ''}`}
                        onClick={() => handleLocaleChange(loc)}
                      >
                        {localeNames[loc]}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button onClick={handleSignOut} className="dashboard-layout__sign-out">
                <LogOut size={20} />
                <span>{t('dashboard.nav.signOut')}</span>
              </button>
            </>
          )}

          {/* Expand button in footer - only when collapsed */}
          {sidebarCollapsed && (
            <button
              className="dashboard-layout__collapse-btn"
              onClick={toggleSidebarCollapse}
              aria-label="Expand sidebar"
              title={t('dashboard.sidebar.expand')}
            >
              <PanelLeft size={20} />
            </button>
          )}
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="dashboard-layout__overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className={`dashboard-layout__main ${sidebarCollapsed ? 'dashboard-layout__main--collapsed' : ''}`}>
        <Outlet />
      </main>
    </div>
  );
}
